import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";
import {
  compradorPublicConfig,
  compradorEventos,
  compradorLogin,
  compradorRegister,
  compradorCreateOrder,
  compradorMisTickets,
} from "../../services/sininApi";

const EMPTY_USER = {
  dni: "", password: "", first_name: "", last_name: "", email: "", whatsapp: "",
};

export default function ComprarEmpresa() {
  const { empresaId } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [comprador, setComprador] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [auth, setAuth] = useState(EMPTY_USER);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [tickets, setTickets] = useState([]);
  const [buy, setBuy] = useState({});

  const colors = useMemo(() => ({
    fondo: empresa?.colorFondo || "#1a1a1a",
    primario: empresa?.colorTicket || "#dfa66b",
    fuente: empresa?.colorFuente || "#ffffff",
    dark: empresa?.colorDark || "#333333",
  }), [empresa]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cfg, evs] = await Promise.all([
          compradorPublicConfig(empresaId),
          compradorEventos(empresaId),
        ]);
        setEmpresa(cfg?.empresa || { empresaId });
        setEventos(Array.isArray(evs?.items) ? evs.items : []);
        const saved = JSON.parse(localStorage.getItem(`comprador_${empresaId}`) || "null");
        if (saved?.dni && saved?.password) {
          const login = await compradorLogin({ empresaId, dni: saved.dni, password: saved.password });
          if (login?.ok) {
            setComprador({ ...login.comprador, password: saved.password });
            cargarTickets(saved.dni, saved.password);
          }
        }
      } catch {
        setError("No pudimos cargar la venta online.");
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line
  }, [empresaId]);

  const cargarTickets = async (dni, password) => {
    try {
      const res = await compradorMisTickets({ empresaId, dni, password });
      setTickets(Array.isArray(res?.items) ? res.items : []);
    } catch {}
  };

  const setAuthField = k => e => setAuth(p => ({ ...p, [k]: e.target.value }));

  const handleAuth = async () => {
    setError("");
    if (!auth.dni || !auth.password) return setError("Completá DNI y contraseña.");
    if (authMode === "register" && (!auth.first_name || !auth.last_name || !auth.email)) {
      return setError("Completá nombre, apellido y email.");
    }
    setWorking(true);
    try {
      const payload = { empresaId, ...auth };
      const res = authMode === "login"
        ? await compradorLogin(payload)
        : await compradorRegister(payload);
      if (res?.error) setError(res.error);
      else {
        const logged = { ...(res.comprador || {}), password: auth.password };
        setComprador(logged);
        localStorage.setItem(`comprador_${empresaId}`, JSON.stringify({
          dni: auth.dni,
          password: auth.password,
        }));
        cargarTickets(auth.dni, auth.password);
      }
    } catch {
      setError("No se pudo ingresar. Intentá de nuevo.");
    }
    setWorking(false);
  };

  const logout = () => {
    localStorage.removeItem(`comprador_${empresaId}`);
    setComprador(null);
    setTickets([]);
  };

  const updateBuy = (eventId, patch) => {
    setBuy(p => ({ ...p, [eventId]: { ...(p[eventId] || {}), ...patch } }));
  };

  const comprar = async (evento) => {
    if (!comprador) return setError("Ingresá con tu DNI antes de comprar.");
    const state = buy[evento.idEvento] || {};
    const tandasPago = evento.tandas || [];
    const tanda = tandasPago.find(t => String(t.id) === String(state.tandaId)) || tandasPago[0];
    if (!tanda) return setError("Este evento no tiene tickets pagos disponibles.");
    setError("");
    setWorking(true);
    try {
      const res = await compradorCreateOrder({
        empresaId,
        dni: comprador.dni,
        password: comprador.password,
        idEvento: evento.idEvento,
        tandaId: tanda.id,
        quantity: Number(state.quantity || 1),
        sexo: state.sexo || "",
        extras: buildExtras(evento, state),
        origin: window.location.origin,
      });
      if (res?.error) setError(res.error);
      else {
        const url = res.init_point || res.sandbox_init_point;
        if (url) window.location.href = url;
        else setError("Mercado Pago no devolvió link de pago.");
      }
    } catch {
      setError("No se pudo crear la compra.");
    }
    setWorking(false);
  };

  if (loading) return (
    <div style={styles.page(colors)}>
      <GlassSpinner size={80} background={colors.fondo} />
    </div>
  );

  return (
    <div style={styles.page(colors)}>
      <header style={styles.header(colors)}>
        {empresa?.logoUrl ? <img src={empresa.logoUrl} alt="" style={styles.logo} /> : <div style={styles.brand}>SININ</div>}
        <div>
          <h1 style={styles.h1}>{empresa?.nombre || empresaId}</h1>
          <p style={styles.subtitle}>Venta online de tickets</p>
        </div>
      </header>

      <main style={styles.wrap}>
        {error && <div style={styles.error}>{error}</div>}

        <section style={styles.card}>
          {!comprador ? (
            <>
              <div style={styles.tabs}>
                <button style={tabStyle(authMode === "login", colors)} onClick={() => setAuthMode("login")}>INGRESAR</button>
                <button style={tabStyle(authMode === "register", colors)} onClick={() => setAuthMode("register")}>CREAR CUENTA</button>
              </div>

              {authMode === "register" && (
                <div style={styles.grid2}>
                  <Input label="Nombre" value={auth.first_name} onChange={setAuthField("first_name")} />
                  <Input label="Apellido" value={auth.last_name} onChange={setAuthField("last_name")} />
                  <Input label="Email" value={auth.email} onChange={setAuthField("email")} />
                  <Input label="WhatsApp" value={auth.whatsapp} onChange={setAuthField("whatsapp")} />
                </div>
              )}
              <div style={styles.grid2}>
                <Input label="DNI" value={auth.dni} onChange={setAuthField("dni")} />
                <Input label="Contraseña" type="password" value={auth.password} onChange={setAuthField("password")} />
              </div>
              <button style={styles.primary(colors)} onClick={handleAuth} disabled={working}>
                {working ? "ESPERE..." : authMode === "login" ? "INGRESAR" : "CREAR CUENTA"}
              </button>
            </>
          ) : (
            <div style={styles.logged}>
              <div>
                <b>{comprador.first_name} {comprador.last_name}</b>
                <div style={{ color: "#777", fontSize: 13 }}>DNI {comprador.dni}</div>
              </div>
              <button style={styles.ghost(colors)} onClick={logout}>SALIR</button>
            </div>
          )}
        </section>

        {comprador && tickets.length > 0 && (
          <section style={styles.card}>
            <h2 style={styles.h2}>MIS TICKETS</h2>
            {tickets.slice(0, 8).map(t => (
              <div key={t.hashSeguro} style={styles.ticketRow}>
                <div>
                  <b>{t.name}</b>
                  <div style={{ fontSize: 13, color: "#777" }}>{t.tanda} · Ticket {t.numeroTicket}/{t.cantidadTotal}</div>
                </div>
                <a href={t.pdf_url} target="_blank" rel="noreferrer" style={styles.smallButton(colors)}>VER PDF</a>
              </div>
            ))}
          </section>
        )}

        <section>
          <h2 style={{ ...styles.h2, color: colors.fuente }}>EVENTOS DISPONIBLES</h2>
          {eventos.length === 0 ? (
            <div style={styles.empty}>No hay eventos con venta online activa.</div>
          ) : eventos.map(evento => {
            const state = buy[evento.idEvento] || {};
            const tanda = (evento.tandas || []).find(t => String(t.id) === String(state.tandaId)) || (evento.tandas || [])[0];
            const quantity = Number(state.quantity || 1);
            const total = Number(tanda?.precio || 0) * quantity;
            return (
              <article key={evento.idEvento} style={styles.eventCard}>
                <h3 style={styles.eventTitle}>{evento.name}</h3>
                <p style={styles.date}>{formatDate(evento.fechaEvento)} · {evento.horaInicioEvento || ""}</p>
                {evento.description && <p style={styles.desc}>{evento.description}</p>}

                <label style={styles.label}>TIPO DE TICKET</label>
                <select style={styles.input} value={state.tandaId || tanda?.id || ""}
                  onChange={e => updateBuy(evento.idEvento, { tandaId: e.target.value })}>
                  {(evento.tandas || []).map(t => (
                    <option key={t.id} value={t.id}>{t.label} - ${Number(t.precio || 0).toLocaleString("es-AR")}</option>
                  ))}
                </select>

                <div style={styles.grid2}>
                  <div>
                    <label style={styles.label}>CANTIDAD</label>
                    <input style={styles.input} type="number" min="1" max={evento.maxPorCompra || 6}
                      value={state.quantity || 1}
                      onChange={e => updateBuy(evento.idEvento, { quantity: e.target.value })} />
                  </div>
                  <div>
                    <label style={styles.label}>SEXO</label>
                    <select style={styles.input} value={state.sexo || ""} onChange={e => updateBuy(evento.idEvento, { sexo: e.target.value })}>
                      <option value="">Seleccionar</option>
                      <option>Femenino</option>
                      <option>Masculino</option>
                      <option>Otro</option>
                    </select>
                  </div>
                </div>

                {(evento.campos || []).filter(c => c.activo !== false).map(campo => (
                  <div key={campo.id}>
                    <label style={styles.label}>{String(campo.label || campo.id).toUpperCase()}</label>
                    <select style={styles.input}
                      value={state[`extra_${campo.id}`] || ""}
                      onChange={e => updateBuy(evento.idEvento, { [`extra_${campo.id}`]: e.target.value })}>
                      <option value="">No</option>
                      <option value="Sí">Sí</option>
                    </select>
                  </div>
                ))}

                <div style={styles.total}>TOTAL: ${total.toLocaleString("es-AR")}</div>
                <button style={styles.primary(colors)} onClick={() => comprar(evento)} disabled={working}>
                  COMPRAR CON MERCADO PAGO
                </button>
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label style={styles.label}>{label.toUpperCase()}</label>
      <input style={styles.input} {...props} />
    </div>
  );
}

function buildExtras(evento, state) {
  const extras = {};
  if (evento.consumicion) extras.consumicion = true;
  (evento.campos || []).forEach(campo => {
    const value = state[`extra_${campo.id}`];
    if (value) extras[campo.id] = value;
  });
  return extras;
}

function formatDate(value) {
  if (!value) return "";
  const parts = String(value).split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}

const tabStyle = (active, colors) => ({
  border: `1px solid ${active ? colors.primario : "#ddd"}`,
  background: active ? colors.primario : "#fff",
  color: active ? "#111" : "#555",
  borderRadius: 25,
  padding: "10px 14px",
  fontWeight: 800,
  cursor: "pointer",
});

const styles = {
  page: c => ({
    minHeight: "100vh",
    background: c.fondo,
    color: c.fuente,
    paddingBottom: 50,
  }),
  header: c => ({
    background: `linear-gradient(135deg, ${c.dark}, ${c.fondo})`,
    padding: "28px 18px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    borderBottom: `4px solid ${c.primario}`,
  }),
  logo: { width: 86, height: 86, objectFit: "contain", background: "transparent" },
  brand: { width: 78, height: 78, borderRadius: 18, display: "grid", placeItems: "center", background: "#fff", color: "#111", fontWeight: 900 },
  h1: { margin: 0, fontSize: 28, textTransform: "uppercase", letterSpacing: 0.5 },
  subtitle: { margin: "4px 0 0", opacity: 0.8 },
  wrap: { maxWidth: 760, margin: "0 auto", padding: 16 },
  card: { background: "#fff", color: "#222", borderRadius: 22, padding: 16, marginBottom: 16, boxShadow: "0 10px 30px rgba(0,0,0,.18)" },
  tabs: { display: "flex", gap: 8, marginBottom: 14 },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 },
  label: { display: "block", fontSize: 11, fontWeight: 900, margin: "10px 0 5px", color: "#555" },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #ddd", borderRadius: 14, padding: "12px 12px", fontSize: 15, outline: "none" },
  primary: c => ({ width: "100%", marginTop: 14, border: "none", borderRadius: 25, background: c.primario, color: c.fuente, padding: "13px 16px", fontWeight: 900, cursor: "pointer" }),
  ghost: c => ({ border: `1px solid ${c.primario}`, borderRadius: 25, background: "#fff", color: "#111", padding: "9px 14px", fontWeight: 900, cursor: "pointer" }),
  logged: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  h2: { margin: "4px 0 12px", fontSize: 17, textTransform: "uppercase" },
  error: { background: "#ffe1e1", color: "#9b1111", borderRadius: 16, padding: 12, marginBottom: 12, fontWeight: 700 },
  empty: { background: "rgba(255,255,255,.12)", color: "#fff", borderRadius: 18, padding: 20, textAlign: "center" },
  eventCard: { background: "#fff", color: "#222", borderRadius: 24, padding: 18, marginBottom: 16, boxShadow: "0 10px 30px rgba(0,0,0,.2)" },
  eventTitle: { margin: 0, fontSize: 23, textTransform: "uppercase" },
  date: { margin: "5px 0", fontWeight: 800, color: "#555" },
  desc: { color: "#666", lineHeight: 1.45 },
  total: { textAlign: "right", marginTop: 14, fontWeight: 900, fontSize: 18 },
  ticketRow: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #eee", padding: "10px 0", gap: 10 },
  smallButton: c => ({ background: c.primario, color: c.fuente, textDecoration: "none", borderRadius: 25, padding: "8px 12px", fontWeight: 900, fontSize: 12 }),
};
